/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { lazy } from 'react';
import { createBrowserRouter } from 'react-router-dom';

import { AppShell } from '@/app/AppShell';
import { AccessGate } from '@/auth/AccessGate';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { load } from './lazyRoutes';

/**
 * A dynamic import failed because the chunk is missing (a stale tab requesting a
 * hashed file that no longer exists after a redeploy), rather than because the
 * module itself threw while evaluating. Only the former is fixed by reloading.
 */
export function isChunkLoadError(err: unknown): boolean {
  const text = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  return /failed to fetch dynamically imported module|error loading dynamically imported module|chunkloaderror|loading chunk \d+ failed/i.test(
    text,
  );
}

/**
 * After a redeploy the hashed chunk names change, so a tab opened before the
 * deploy still references the old file and its dynamic import 404s ("Failed to
 * fetch dynamically imported module"). Recover by reloading once to pull the
 * fresh index.html + manifest, instead of crashing to the router error page.
 * Genuine module-evaluation errors are rethrown so they aren't masked, and a
 * short time-gate prevents a reload loop when the fetch failure is persistent
 * (offline, real 500, etc.).
 */
export function withReload<T>(factory: () => Promise<T>): () => Promise<T> {
  return () =>
    factory().catch((err) => {
      if (!isChunkLoadError(err)) throw err;
      const KEY = 'chunkReloadAt';
      let last = 0;
      try {
        last = Number(sessionStorage.getItem(KEY)) || 0;
      } catch {
        /* storage unavailable */
      }
      if (Date.now() - last > 10_000) {
        try {
          sessionStorage.setItem(KEY, String(Date.now()));
        } catch {
          /* ignore */
        }
        window.location.reload();
        return new Promise<T>(() => {}); // hang until the reload takes over
      }
      throw err;
    });
}

const ManufacturingHomePage = lazy(withReload(load['/']));
const RepairOverviewPage = lazy(withReload(load['/repair-overview']));
const OnlinePage = lazy(withReload(load['/online']));
const MmtDetailPage = lazy(withReload(load['/mmt']));
const OfflinePage = lazy(withReload(load['/offline']));
const GsipOverviewPage = lazy(withReload(load['/gsip']));
const DtcSearchPage = lazy(withReload(load['/dtc']));
const DiagnosticPage = lazy(withReload(load['/diagnostic']));
const ExplorerPage = lazy(withReload(load['/schematic-explorer']));
const MeclPage = lazy(withReload(load['/mecl']));
const Viewer3DPage = lazy(withReload(load['/viewer-3d']));
const ResourceLinksPage = lazy(withReload(load['/resource-links']));
const EngineeringDiagnosticsPage = lazy(withReload(load['/engineering-diagnostics']));
const PartSearchPage = lazy(withReload(load['/engineering-diagnostics/parts']));
const FailureAnalyticsPage = lazy(withReload(load['/engineering-diagnostics/failures']));
const EngineeringAssistantPage = lazy(withReload(load['/engineering-diagnostics/assistant']));
const AnalyticsPage = lazy(withReload(load['/analytics']));
const AdminPage = lazy(withReload(load['/admin']));
const UnauthorizedPage = lazy(withReload(load['/unauthorized']));
const NotFoundPage = lazy(withReload(load['*']));

export const router = createBrowserRouter([
  // Reachable without a session so a 401 (expired/missing token, or no access) has somewhere to
  // land instead of the app silently degrading. Deliberately outside the ProtectedRoute guard.
  { path: '/unauthorized', element: <UnauthorizedPage /> },
  {
    path: '/',
    element: (
      // Two gates, two questions: ProtectedRoute asks who you are, AccessGate asks whether this
      // deployment has anything for you. Both sit above the shell so neither renders a rail of
      // destinations that would refuse the visitor.
      <ProtectedRoute>
        <AccessGate>
          <AppShell />
        </AccessGate>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <ManufacturingHomePage /> },
      { path: 'repair-overview', element: <RepairOverviewPage /> },
      { path: 'online', element: <OnlinePage /> },
      { path: 'mmt/:mmtId', element: <MmtDetailPage /> },
      { path: 'offline', element: <OfflinePage /> },
      { path: 'gsip', element: <GsipOverviewPage /> },
      { path: 'dtc', element: <DtcSearchPage /> },
      { path: 'diagnostic', element: <DiagnosticPage /> },
      { path: 'schematic-explorer', element: <ExplorerPage /> },
      { path: 'mecl', element: <MeclPage /> },
      { path: 'viewer-3d', element: <Viewer3DPage /> },
      { path: 'resource-links', element: <ResourceLinksPage /> },
      { path: 'engineering-diagnostics', element: <EngineeringDiagnosticsPage /> },
      { path: 'engineering-diagnostics/parts', element: <PartSearchPage /> },
      { path: 'engineering-diagnostics/failures', element: <FailureAnalyticsPage /> },
      { path: 'engineering-diagnostics/assistant', element: <EngineeringAssistantPage /> },
      { path: 'analytics', element: <AnalyticsPage /> },
      { path: 'admin/*', element: <AdminPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
